import { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Spinner } from "@/components/ui/spinner";
import RouteSeo from "@/components/seo/RouteSeo";
import { lazyRetry } from "@/lib/lazyRetry";
import { AuthProvider } from "@/hooks/useAuth";
import AppShellRoutes from "./AppShellRoutes";

const Landing = lazyRetry(() => import("./pages/Landing"));
const Auth = lazyRetry(() => import("./pages/Auth"));
const Toaster = lazyRetry(() =>
  import("@/components/ui/toaster").then((module) => ({
    default: module.Toaster,
  })),
);
const Sonner = lazyRetry(() =>
  import("@/components/ui/sonner").then((module) => ({
    default: module.Toaster,
  })),
);
const SpeedInsights = lazyRetry(() =>
  import("@vercel/speed-insights/react").then((module) => ({
    default: module.SpeedInsights,
  })),
);

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Spinner size={32} className="text-primary" />
  </div>
);

const AuthStandaloneRoute = () => (
  <AuthProvider>
    <Auth />
  </AuthProvider>
);

const DeferredTelemetry = () => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;
    let idleId: number | null = null;

    type NetworkInformationLike = {
      saveData?: boolean;
    };

    const navWithConnection = navigator as Navigator & {
      connection?: NetworkInformationLike;
      mozConnection?: NetworkInformationLike;
      webkitConnection?: NetworkInformationLike;
    };

    const connection =
      navWithConnection.connection ||
      navWithConnection.mozConnection ||
      navWithConnection.webkitConnection;

    if (connection?.saveData) {
      return;
    }

    const enableTelemetry = () => {
      if (!cancelled) {
        setEnabled(true);
      }
    };

    const idleApi = window as Window & {
      requestIdleCallback?: (
        callback: () => void,
        options?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (typeof idleApi.requestIdleCallback === "function") {
      idleId = idleApi.requestIdleCallback(enableTelemetry, { timeout: 4000 });
    } else {
      timeoutId = window.setTimeout(enableTelemetry, 2500);
    }

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (idleId !== null && typeof idleApi.cancelIdleCallback === "function") {
        idleApi.cancelIdleCallback(idleId);
      }
    };
  }, []);

  if (!enabled) return null;

  return (
    <Suspense fallback={null}>
      <SpeedInsights />
    </Suspense>
  );
};

const DeferredToasts = () => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;
    let idleId: number | null = null;

    const enableToasts = () => {
      if (!cancelled) {
        setEnabled(true);
      }
    };

    const idleApi = window as Window & {
      requestIdleCallback?: (
        callback: () => void,
        options?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    const enableOnFirstInteraction = () => {
      enableToasts();
    };

    window.addEventListener("pointerdown", enableOnFirstInteraction, {
      once: true,
      passive: true,
    });
    window.addEventListener("keydown", enableOnFirstInteraction, {
      once: true,
    });

    if (typeof idleApi.requestIdleCallback === "function") {
      idleId = idleApi.requestIdleCallback(enableToasts, { timeout: 8000 });
    } else {
      timeoutId = window.setTimeout(enableToasts, 8000);
    }

    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", enableOnFirstInteraction);
      window.removeEventListener("keydown", enableOnFirstInteraction);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (idleId !== null && typeof idleApi.cancelIdleCallback === "function") {
        idleApi.cancelIdleCallback(idleId);
      }
    };
  }, []);

  if (!enabled) return null;

  return (
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
    </Suspense>
  );
};

const App = () => (
  <ErrorBoundary>
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem={false}
      forcedTheme="dark"
    >
      <DeferredToasts />
      <DeferredTelemetry />
      <BrowserRouter>
        <RouteSeo />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<AuthStandaloneRoute />} />
            <Route path="*" element={<AppShellRoutes />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
